import React, { useEffect, useState } from "react";
import moment from "moment";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useModal from "hooks/modals/useModal";
import { useStripeTerminalPayment } from "hooks/useStripeTerminalPayment/useStripeTerminalPayment";

import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess, enqueue, dequeue } from "redux/actions/ui";

import {
  GetInvoiceTransaction,
  GetInvoiceTransactionReceipt,
  IGetInvoiceTransaction,
  PostInvoiceTransactionRefund,
} from "api/rpc/2024-04/facilityAdmin/order/invoice";
import {
  GetBookingDeposit,
  IBookingDeposit,
  PostBookingDepositRefund,
  GetBookingDepositReceipt,
} from "api/rpc/2024-04/facilityAdmin/deposit/deposit";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetEvent } from "api/rpc/2024-04/facilityAdmin/event/hospitalityEvent";
import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import DataTable from "../../../../../components/dataTable/DataTable";
import { LocaleCurrency } from "helpers/Locale";
import { Badge } from "components/badge/Badge";
import InvoicePaymentModal from "./InvoicePaymentModal";
import { IInvoiceTransaction } from "redux/reducers/models/order";
import { loadPaymentOptions } from "redux/actions/facility";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import YearDropFilter from "components/filters/YearDropFilter";
import { THIS_YEAR } from "components/form/calendarField/helpers";
import Tabs from "components/tabs/Tabs";
import { capitalize, convertTime, errorMessage, formatDate, valueToString } from "helpers/Helpers";
import Card from "components/card/Card";
import Input from "components/form/input";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import useDebounce from "hooks/events/useDebounce";
import { ButtonNew as Button } from "components/buttonNew";
import { faBarsFilter, faXmark } from "@fortawesome/pro-regular-svg-icons";
import DepositFiltersDrawer, { IDepositFilters } from "./drawers/DepositFiltersDrawer";
import "./deposits.scss";
import { GetCart, PostCheckout, PutVoid } from "api/rpc/2024-04/facilityAdmin/cart/cart";
import { ICart } from "redux/reducers/models/cart";
import { PostCart } from "api/rpc/2024-04/facilityAdmin/order/cart/cart";
import { PostLineItemToCart } from "api/rpc/2024-04/facilityAdmin/order/cart/lineItem";
import { PostTransaction } from "api/rpc/2024-04/facilityAdmin/order/transaction";
import { GetOrder, PutOrderComplete, PutVoidOrder } from "api/rpc/2024-04/facilityAdmin/order/order";
import { GetBooking, PutBookingParticipant } from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
interface IDepositState {
  modalOpen: boolean;
  eventSearch: string;
  amount: number;
}

const TABLE_LIMIT = 50;
const BOOKING_TABLE_LIMIT = 50;

export default function Deposits() {
  const dispatch = useAppDispatch();
  const { terminalStore, facilityStore } = useAppSelector(store => store);
  const { processRefund } = useStripeTerminalPayment();

  const [deposits, setDeposits] = useState<Array<IInvoiceTransaction>>(undefined);
  const [bookingDeposits, setBookingDeposits] = useState<Array<IBookingDeposit>>(undefined);
  const [depositsRefresh, setDepositsRefresh] = useState(false);

  const [filterState, setFilterState] = useState<IGetInvoiceTransaction>({
    offset: 0,
    year: THIS_YEAR,
  });

  const [depositFiltersDrawerOpen, setDepositFiltersDrawerOpen] = useState<boolean>(false);

  const [bookingFilterState, setBookingFilterState] = useState<IDepositFilters>({
    offset: 0,
    search: "",
    booking_date: null,
    deposit_date: null,
    min_amount: "",
    max_amount: "",
    status: null,
  });

  const debouncedBookingDepositSearch = useDebounce(bookingFilterState?.search);

  const [events, setEvents] = useState<any>(null);
  const { t } = useTranslation();
  const [depositState, setDepositState] = useState<IDepositState>({
    modalOpen: false,
    eventSearch: "",
    amount: 0,
  });

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const {
    state: refundPopup,
    closeModal: closeRefundPopup,
    updateModal: updateRefundPopup,
  } = useModal({
    clearOnClose: true,
    transaction: null,
    amount: 0,
  });

  const {
    state: bookingRefundPopup,
    closeModal: closeBookingRefundPopup,
    updateModal: updateBookingRefundPopup,
  } = useModal({
    clearOnClose: true,
    deposit: null,
    amount: 0,
  });

  const {
    state: applyDepositPopup,
    closeModal: closeApplyDepositPopup,
    updateModal: updateApplyDepositPopup,
  } = useModal<{ deposit: IBookingDeposit }>({
    deposit: null,
  });

  const accountTabs = [
    {
      id: "invoice-deposits",
      content: t("Invoice Deposits"),
    },
    {
      id: "booking-deposits",
      content: t("Booking Deposits"),
    },
  ];

  // Load payment methods being used in modal
  useEffect(() => {
    const source = axios.CancelToken.source();
    void dispatch(loadPaymentOptions(source.token));
    return () => source.cancel();
  }, []);

  // Load table data
  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadDeposits(source.token);
    return () => source.cancel();
  }, [filterState, depositsRefresh]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadBookingDeposits(source.token);
    return () => source.cancel();
  }, [
    bookingFilterState?.offset,
    bookingFilterState?.status,
    bookingFilterState?.min_amount,
    bookingFilterState?.max_amount,
    bookingFilterState?.booking_date,
    bookingFilterState?.deposit_date,
    debouncedBookingDepositSearch,
    depositsRefresh]);

  // Load events when modal opens
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (depositState.modalOpen) {
      void loadEvents(source.token);
    }

    return () => {
      source.cancel();
    };
  }, [depositState.eventSearch, depositState.modalOpen]);

  async function loadDeposits(token?: CancelToken) {
    if (deposits !== undefined) {
      setDeposits(undefined);
    }

    const res = await GetInvoiceTransaction(
      {
        all_deposits: true,
        refunds: true,
        limit: TABLE_LIMIT,
        ...filterState,
      },
      token ? false : true,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.deposits.001")));
    }

    setDeposits(res.status !== StatusCode.OK ? [] : res.data);
  }

  async function loadBookingDeposits(token?: CancelToken) {
    if (bookingDeposits !== undefined) {
      setBookingDeposits(undefined);
    }

    const filters = buildFilters();

    const res = await GetBookingDeposit(
      {
        filters: filters?.length > 0 ? filters : undefined,
        offset: bookingFilterState?.offset,
        search: bookingFilterState?.search ? bookingFilterState?.search : undefined,
        limit: BOOKING_TABLE_LIMIT,
        customer: true,
        booking: true,
        refunds: true,
      }, false, token,
    );

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading booking deposits")));
    }

    setBookingDeposits(res?.data);
  }

  function buildFilters() {
    const filters = [];
    if (bookingFilterState?.status && bookingFilterState?.status?.length > 0) {
      filters?.push({ label: "status", value: bookingFilterState?.status });
    }
    if (bookingFilterState?.min_amount !== "" && bookingFilterState?.min_amount != null && !isNaN(Number(bookingFilterState?.min_amount))) {
      filters?.push({ label: "min_amount", value: Number(bookingFilterState?.min_amount) });
    }
    if (bookingFilterState?.max_amount !== "" && bookingFilterState?.max_amount != null && !isNaN(Number(bookingFilterState?.max_amount))) {
      filters?.push({ label: "max_amount", value: Number(bookingFilterState?.max_amount) });
    }
    if (bookingFilterState?.booking_date) {
      filters?.push({ label: "booking_date", value: formatDate(bookingFilterState?.booking_date) });
    }
    if (bookingFilterState?.deposit_date) {
      filters?.push({ label: "deposit_date", value: formatDate(bookingFilterState?.deposit_date) });
    }

    return filters;
  }

  async function loadEvents(token: CancelToken) {
    let tournaments: Array<any> = [];
    let events: Array<any> = [];
    setEvents(null);

    const tournamentRes = await GetTournament({ search: depositState.eventSearch }, false, token);

    if (token && token.reason) {
      return;
    }
    if (tournamentRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.deposits.002")));
    } else {
      tournaments = tournamentRes.data.map(tournament => ({ ...tournament, event_type: "tournament" }));
    }

    const eventRes = await GetEvent({ search: depositState.eventSearch }, false, token);

    if (token && token.reason) {
      return;
    }
    if (eventRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.deposits.003")));
    } else {
      events = eventRes.data.map(event => ({ ...event, event_type: "hospitality" }));
    }

    const allEvents = tournaments.concat(events);

    setEvents(allEvents);
  }

  async function getDepositReceipt(transactionId: number) {
    const res = await GetInvoiceTransactionReceipt({ transaction_id: transactionId }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.deposits.004")));
      return;
    }

    window.open().document.write(String(res.data));
  }

  async function getBookingDepositReceipt(depositId: number) {
    const res = await GetBookingDepositReceipt({ deposit_id: depositId }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error printing booking deposit receipt")));
      return;
    }

    window.open().document.write(String(res.data));
  }

  async function refundDeposit() {
    if (refundPopup.amount > refundPopup.transaction?.amount_remaining || refundPopup.amount <= 0) {
      dispatch(showError(t("Invalid refund amount")));
      return;
    }

    if (refundPopup.transaction?.payment_type === "interac") {
      if (terminalStore?.reader) {
        dispatch(enqueue());
        const refundRes = await processRefund(
          Number(refundPopup.amount),
          String(refundPopup.transaction?.payment_intent_id),
        );
        if (refundRes === "success") {
          dispatch(dequeue());
          dispatch(showSuccess(t("secure.facility.order.invoices.deposits.005")));
        } else {
          dispatch(dequeue());
          dispatch(showSuccess(t("secure.facility.order.invoices.deposits.006")));
          return;
        }
      } else {
        dispatch(showSuccess(t("secure.facility.order.invoices.deposits.007")));
        return;
      }
    }

    const res = await PostInvoiceTransactionRefund({ transaction_id: refundPopup.transaction?.id, amount: refundPopup.amount }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.deposits.008")));
      return;
    }

    closeRefundPopup();
    void loadDeposits();
  }

  async function refundBookingDeposit() {
    if (bookingRefundPopup.amount > bookingRefundPopup.deposit?.amount_remaining || bookingRefundPopup.amount < 0) {
      dispatch(showError(t("Invalid refund amount")));
      return;
    }

    if (bookingRefundPopup.deposit?.payment_type === "interac") {
      if (terminalStore?.reader) {
        dispatch(enqueue());
        const refundRes = await processRefund(
          Number(bookingRefundPopup.deposit?.amount),
          String(bookingRefundPopup.deposit?.payment_intent_id),
        );
        if (refundRes === "success") {
          dispatch(dequeue());
          dispatch(showSuccess(t("secure.facility.order.invoices.deposits.005")));
        } else {
          dispatch(dequeue());
          dispatch(showSuccess(t("secure.facility.order.invoices.deposits.006")));
          return;
        }
      } else {
        dispatch(showSuccess(t("secure.facility.order.invoices.deposits.007")));
        return;
      }
    }

    const res = await PostBookingDepositRefund({ deposit_id: bookingRefundPopup.deposit?.id, amount: bookingRefundPopup.amount }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error refunding booking deposit")));
      return;
    }

    closeBookingRefundPopup();
    void loadBookingDeposits();
  }

  function handleTableNavigation(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset - TABLE_LIMIT }));
        return;
      }
      case "next": {
        setFilterState(prev => ({ ...prev, offset: filterState.offset + TABLE_LIMIT }));
        return;
      }
      default:
        return;
    }
  }

  function handleBookingTableNavigation(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        setBookingFilterState(prev => ({ ...prev, offset: bookingFilterState.offset - BOOKING_TABLE_LIMIT }));
        return;
      }
      case "next": {
        setBookingFilterState(prev => ({ ...prev, offset: bookingFilterState.offset + BOOKING_TABLE_LIMIT }));
        return;
      }
      default:
        return;
    }
  }

  const toggleDepositRefresh = () => setDepositsRefresh(prev => !prev);

  const primaryAction = {
    content: t("secure.facility.order.invoices.deposits.009"),
    action: () => setDepositState(prevState => ({ ...prevState, modalOpen: true })),
  };

  const bookingDepositFilterApplied = bookingFilterState?.booking_date
    || bookingFilterState?.deposit_date || bookingFilterState?.max_amount || bookingFilterState?.min_amount || bookingFilterState?.status?.length > 0;

  function getFilterText(key: keyof IDepositFilters) {
    if (key === "booking_date") {
      return `${t("Booking date")}: ${moment(bookingFilterState[key])?.format("ll")}`;
    } else if (key === "deposit_date") {
      return `${t("Deposit date")}: ${moment(bookingFilterState[key])?.format("ll")}`;
    } else if (key === "status") {
      return `${t("Status")}: ${bookingFilterState[key]?.map(s => valueToString(s))?.join(", ")}`;
    } else if (key === "min_amount" || key === "max_amount") {
      return <div>{t("Amount: ")} {bookingFilterState?.min_amount ? <LocaleCurrency currency={facilityStore?.facility?.default_currency_code} amount={bookingFilterState?.min_amount} /> : null} {bookingFilterState?.min_amount && bookingFilterState?.max_amount ? " - " : null} {bookingFilterState?.max_amount ? <LocaleCurrency currency={facilityStore?.facility?.default_currency_code} amount={bookingFilterState?.max_amount} /> : null}</div>;
    }
    return key;
  }

  async function handleApplyDeposit(deposit: IBookingDeposit) {
    const depositPaymentOption = facilityStore?.paymentOptions?.find(option => option?.type === "deposit");
    if (!depositPaymentOption) {
      dispatch(showError(t("Facility deposit payment option not set. Setup the deposit payment option and try again.")));
      return;
    }

    let cartId: number = null;
    let orderId: number = null;
    try {
      dispatch(enqueue());

      // Get cart used to build the booking order
      const cartRes = await GetCart({ id: deposit?.booking?.cart_id }, false);
      if (cartRes?.status !== StatusCode.OK || !cartRes?.data[0]) {
        throw new Error(errorMessage(cartRes, t("Error getting deposit info")));
      }
      const cart: ICart = cartRes?.data[0];
      const totalCartAmount = cart?.line_items?.reduce((total, lineItem) => {
        if (lineItem?.product?.type === "Booking Fee") {
          return total;
        } else {
          return total + lineItem?.total_price;
        }
      }, 0);

      if (Number(totalCartAmount?.toFixed(2)) !== Number(deposit?.amount_remaining?.toFixed(2))) {
        throw new Error(t("Deposit amount does not cover the booking total amount"));
      }

      // Get variant ids of all Green Fee / Power Cart products (excludes Booking Fees)
      const lineItemsToAdd = cart?.line_items?.filter(lineItem => lineItem?.product?.type !== "Booking Fee")?.map(lineItem => lineItem);

      // Create a new cart
      const newCartRes = await PostCart({ customer_id: cart?.customer_id }, false);
      if (newCartRes?.status !== StatusCode.OK) {
        throw new Error(errorMessage(newCartRes, t("Error creating new cart")));
      }

      cartId = newCartRes?.data?.id;

      // Add the Green Fee / Power Cart products to the cart
      const lineItemsRes = await PostLineItemToCart({ cart_id: cartId, variants: lineItemsToAdd?.map(lineItem => ({ variant_id: lineItem?.variant_id, quantity: lineItem?.quantity })) }, false);
      if (lineItemsRes?.status !== StatusCode.OK) {
        throw new Error(errorMessage(lineItemsRes, t("Error adding line items to cart")));
      }

      const register = JSON.parse(localStorage.getItem("register"));
      const registerId = register?.id;

      const checkoutRes = await PostCheckout({ cart_id: cartId, register_id: registerId }, false);
      if (checkoutRes?.status !== StatusCode.OK) {
        throw new Error(errorMessage(checkoutRes, t("Error checking out cart")));
      }

      let { order } = checkoutRes.data;

      orderId = order?.id;

      const transactionRes = await PostTransaction(
        {
          kind: "sale",
          amount: deposit?.amount_remaining,
          order_id: orderId,
          payment_option_id: depositPaymentOption?.id,
          deposit_id: deposit?.id,
        },
        false,
      );

      if (transactionRes?.status !== StatusCode.OK) {
        throw new Error(errorMessage(transactionRes, t("Error creating deposit transaction")));
      }

      const orderRes = await GetOrder({ id: orderId }, false);
      if (orderRes?.status !== StatusCode.OK) {
        throw new Error(errorMessage(orderRes, t("Error getting order info")));
      }

      order = orderRes?.data[0];
      orderId = order?.id;

      if (order.balance > 0 || order.financial_status !== "paid") {
        throw new Error(t("Deposit does not fully pay the order."));
      }

      // Complete the order
      const completeRes = await PutOrderComplete(
        {
          order_id: orderId,
          register_id: registerId,
          gift_cards: undefined,
        },
        false,
      );

      if (completeRes?.status !== StatusCode.OK) {
        throw new Error(errorMessage(completeRes, t("Error completing order")));
      }

      const bookingRes = await GetBooking({ id: deposit?.booking?.id, extended: true }, false);
      if (bookingRes?.status !== StatusCode.OK || bookingRes?.data?.length === 0) {
        throw new Error(errorMessage(bookingRes, t("Error getting booking details")));
      }

      const booking = bookingRes?.data[0];

      const participantIds = booking?.participants?.map(participant => participant?.id);
      const greenFeePaid = lineItemsToAdd?.some(item => item?.product?.type === "Green Fee");
      const powerCartPaid = lineItemsToAdd?.some(item => item?.product?.type === "Power Cart");
      const participantRes = await Promise.all(participantIds?.map(id => PutBookingParticipant({ id: id, order_id: orderId, green_fee_paid: greenFeePaid, power_cart_paid: powerCartPaid }, false)));
      if (participantRes?.some(res => res.status !== StatusCode.OK)) {
        throw new Error(errorMessage(bookingRes, t("Error updating participant details")));
      }

      dispatch(showSuccess(t("Successfully applied booking deposit")));

      // Refresh deposits
      await loadBookingDeposits();

    } catch (e) {
      // Error occured, void the order or cart
      try {
        if (orderId) {
          // Order failed, void the order
          const orderVoidRes = await PutVoidOrder({ order_id: orderId, void_note: t("Error when applying deposit") }, false);
          if (orderVoidRes?.status !== StatusCode.OK) {
            throw new Error(errorMessage(orderVoidRes, t("Error voiding order")));
          }
        } else if (cartId) {
          // Cart failed, void the cart
          const cartVoidRes = await PutVoid({ cart_ids: [cartId], cancel_reason: t("Error when applying deposit") }, false);
          if (cartVoidRes?.status !== StatusCode.OK) {
            throw new Error(errorMessage(cartVoidRes, t("Error voiding cart")));
          }
        }
        dispatch(showError(e instanceof Error ? e.message : t("An error occurred when applying the deposit")));
      } catch (cleanupError) {
        console.error(cleanupError);
        dispatch(showError(t("An error occurred when applying the deposit")));
      }
    } finally {
      dispatch(dequeue());
      void closeApplyDepositPopup();
    }
  }

  return (
    <Page title={t("secure.facility.order.invoices.deposits.010")} primaryAction={selectedTabIndex === 0 ? primaryAction : undefined} subtitle={t("View and manage invoice and booking deposits for your facility here.")}>
      <Tabs tabs={accountTabs} selected={selectedTabIndex} onSelect={value => setSelectedTabIndex(value)} variant="underline" />
      <br />
      {selectedTabIndex === 0 && (
        <>
          <div className="flex flex-row flex-wrap gap-4 pb-4">
            <YearDropFilter
              handleApply={year => setFilterState({ ...filterState, year: year })}
              defaultCheckboxes={[filterState.year]}
              leftAligned
            />
          </div>
          <DataTable
            columns={[
              { label: t("secure.facility.order.invoices.deposits.011") },
              { label: t("secure.facility.order.invoices.deposits.012") },
              { label: t("Amount Remaining") },
              { label: t("secure.facility.order.invoices.deposits.013") },
              { label: t("secure.facility.order.invoices.deposits.014") },
              { label: "", width: "5%" },
            ]}
            loading={deposits === undefined}
            footer={{
              tableLimit: TABLE_LIMIT,
              tableOffset: filterState.offset,
              handleTableOffset: direction => handleTableNavigation(direction),
              disableNextOffset: !(deposits?.length === TABLE_LIMIT) || deposits === undefined,
            }}
          >
            {deposits
              ?.filter(deposit => deposit.kind !== "refund")
              ?.map(deposit => (
                <tr key={deposit.id}>
                  <td>{eventName(deposit)}</td>
                  <td>
                    <LocaleCurrency currency={deposit.currency} amount={deposit.amount} />
                  </td>
                  <td>
                    <LocaleCurrency currency={deposit.currency} amount={deposit.amount_remaining} />
                  </td>
                  <td>
                    <span>{moment(deposit.processed_at).format("MMM DD, YYYY")}</span>
                  </td>
                  <td>{statusBadge(deposit, t)}</td>
                  <td>
                    <NavigationDropdownNew
                      showPlainTextLabel
                      rightAlign
                      label={<FontAwesomeIcon icon={["far", "chevron-down"]} />}
                      sections={[
                        [
                          {
                            icon: "print",
                            label: t("secure.facility.order.invoices.deposits.015"),
                            onClick: () => void getDepositReceipt(deposit.id),
                          },
                          {
                            icon: "turn-down-left",
                            label: t("secure.facility.order.invoices.deposits.016"),
                            onClick: () => updateRefundPopup({ isOpen: true, transaction: deposit, amount: deposit.amount_remaining }),
                            disabled: deposit.refunded || deposit.kind === "refund" || deposit.amount_remaining <= 0 || deposit.invoice_id !== null,
                          },
                        ],
                      ]}
                    />
                  </td>
                </tr>
              ))}

          </DataTable>
        </>
      )}

      {selectedTabIndex === 1 && (
        <>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                onSearchClear={() => setBookingFilterState(prev => ({ ...prev, search: "" }))}
                placeholder={t("Search by customer...")}
                value={bookingFilterState?.search}
                onChange={e => setBookingFilterState(prev => ({ ...prev, search: e.target.value }))} />
            </div>
            <Button size="medium" type="secondary" onClick={() => setDepositFiltersDrawerOpen(true)}><FontAwesomeIcon icon={faBarsFilter} className="mr-2" /> {t("Filter by")}</Button>
          </div>
          <br />
          {bookingDepositFilterApplied ? (
            <>
              <div className="deposits-table-tag-container">
                {Object.keys(bookingFilterState)?.filter((key: keyof IDepositFilters) =>
                  key !== "search" && key !== "offset" && bookingFilterState[key])?.map((key: keyof IDepositFilters, index) => {
                    if (key === "max_amount" && bookingFilterState?.min_amount && bookingFilterState?.max_amount) {
                      return;
                    }
                    return (
                      <div key={index} className="deposits-table-tag">
                        <p className="deposits-table-tag-text">{getFilterText(key)}</p>
                        <FontAwesomeIcon icon={faXmark} className="deposits-table-tag-x" onClick={() => {
                          if ((key === "min_amount" || key === "max_amount") && (bookingFilterState?.min_amount && bookingFilterState?.max_amount)) {
                            setBookingFilterState(prev => ({ ...prev, min_amount: "", max_amount: "" }));
                          } else {
                            setBookingFilterState(prev => ({ ...prev, [key]: null }));
                          }
                        }} />
                      </div>
                    );
                  })}
                <Button size="small" type="text" onClick={() => {
                  setBookingFilterState(prev => ({
                    ...prev,
                    booking_date: null,
                    deposit_date: null,
                    min_amount: "",
                    max_amount: "",
                    status: null,
                  }));
                }}><FontAwesomeIcon icon={faXmark} className="mr-2" /> {t("Clear all")}</Button>
              </div>
              <br />
            </>
          ) : null}
          <Card>
            <Card.Section table>
              <DataTable
                columns={[
                  { label: t("Booking") },
                  { label: t("Customer") },
                  { label: t("Amount") },
                  { label: t("Amount Remaining") },
                  { label: t("Processed On") },
                  { label: t("Status") },
                  { label: "", width: "5%" },
                ]}
                loading={bookingDeposits === undefined}
                footer={{
                  tableLimit: BOOKING_TABLE_LIMIT,
                  tableOffset: bookingFilterState.offset,
                  handleTableOffset: direction => handleBookingTableNavigation(direction),
                  disableNextOffset:
                    !(bookingDeposits?.length === BOOKING_TABLE_LIMIT) || bookingDeposits === undefined,
                }}
              >
                {bookingDeposits?.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <MessageDisplay icon="circle-minus" title={t("No booking deposits yet")} content={t("Booking deposits will appear here automatically when customers prepay for a booking.")} />
                    </td>
                  </tr>
                ) : bookingDeposits
                  ?.filter(deposit => deposit.kind !== "refund")
                  ?.map((deposit, index, depositsArray) => (
                    <tr key={deposit.id}>
                      <td>
                        <span>{moment(deposit.booking?.tee_time?.date).format("MMMM DD YYYY")}</span>
                        <span> {t("at")} {convertTime(deposit.booking?.tee_time?.start_time)}</span>
                      </td>
                      <td>{(deposit.customer?.first_name ?? "") + " " + (deposit.customer?.last_name ?? "")}</td>
                      <td>{<LocaleCurrency currency={deposit.currency} amount={deposit.amount} />}</td>
                      <td>{<LocaleCurrency currency={deposit.currency} amount={deposit.amount_remaining} />}</td>
                      <td>{moment(deposit.processed_at_local).format("MMM DD, YYYY")}</td>
                      <td>{bookingDepositStatusBadge(deposit, t)}</td>
                      <td>
                        <NavigationDropdownNew
                          showPlainTextLabel
                          rightAlign
                          label={<FontAwesomeIcon icon={["far", "chevron-down"]} />}
                          position={index + 1 === depositsArray?.length ? "top" : "bottom"}
                          sections={[
                            [
                              {
                                icon: "print",
                                label: t("secure.facility.order.invoices.deposits.015"),
                                onClick: () => void getBookingDepositReceipt(deposit.id),
                              },
                              {
                                icon: "turn-down-left",
                                label: t("secure.facility.order.invoices.deposits.016"),
                                onClick: () =>
                                  updateBookingRefundPopup({
                                    isOpen: true,
                                    deposit: deposit,
                                    amount: deposit.amount_remaining || 0,
                                  }),
                                disabled: deposit.refunded || deposit.kind === "refund",
                              },
                              {
                                icon: "circle-check",
                                label: t("Apply"),
                                onClick: () => updateApplyDepositPopup({ isOpen: true, deposit: deposit }),
                                hidden: !facilityStore?.paymentOptions?.some(option => option?.type === "deposit")
                                  || deposit?.status !== "pending"
                                  || deposit?.amount_remaining <= 0
                                  || deposit?.amount !== deposit?.amount_remaining
                                  || !deposit?.booking?.pre_paid, // Partial deposit, cannot apply to the full booking total
                              },
                            ],
                          ]}
                        />
                      </td>
                    </tr>
                  ))}

              </DataTable>
            </Card.Section>
          </Card>
        </>
      )
      }

      <DepositFiltersDrawer
        open={depositFiltersDrawerOpen}
        filters={bookingFilterState}
        onClose={() => setDepositFiltersDrawerOpen(false)}
        onOk={filters => setBookingFilterState(prev => ({ ...prev, ...filters }))}
      />

      <InvoicePaymentModal
        paymentModalVisible={depositState.modalOpen}
        paymentMethods={null} // TODO: Load current customer payment methods
        onOk={() => {
          setDepositState(prevState => ({ ...prevState, modalOpen: false }));
          toggleDepositRefresh();
        }}
        closePaymentModal={() => setDepositState(prevState => ({ ...prevState, modalOpen: false }))}
        depositOnly
        events={events}
        onEventSearch={(value: string) => setDepositState(prevState => ({ ...prevState, eventSearch: value }))}
        eventSearchValue={depositState.eventSearch}
      />

      <Portal isMounted={refundPopup.isOpen}>
        <Popup
          open={refundPopup.isOpen}
          type="warning"
          title={t("secure.facility.order.invoices.deposits.017")}
          description={t("secure.facility.order.invoices.deposits.018")}
          onCancel={closeRefundPopup}
          onOk={() => refundDeposit()}
          okText={t("secure.facility.order.invoices.deposits.016")}
        >
          <div className="pt-2">
            <Input
              type="number"
              label="Refund Amount"
              value={refundPopup.amount}
              onChange={e => updateRefundPopup({ amount: Number(e.target.value) })}
            />
          </div>
        </Popup>
      </Portal>

      <Portal isMounted={bookingRefundPopup.isOpen}>
        <Popup
          open={bookingRefundPopup.isOpen}
          type="warning"
          title={t("Refund Booking Deposit")}
          description={t("Are you sure you want to refund this booking deposit?")}
          onCancel={closeBookingRefundPopup}
          onOk={() => refundBookingDeposit()}
          okText={t("Refund")}
        >
          <div className="pt-2">
            <Input
              type="number"
              label="Refund Amount"
              value={bookingRefundPopup.amount}
              onChange={e => updateBookingRefundPopup({ amount: Number(e.target.value) })}
            />
          </div>
        </Popup>
      </Portal>
      <Portal isMounted={applyDepositPopup?.isOpen}>
        <Popup
          open={applyDepositPopup.isOpen}
          type="warning"
          title={t("Apply Booking Deposit")}
          description={t("Are you sure you want to apply this booking deposit?")}
          onCancel={closeApplyDepositPopup}
          onOk={() => handleApplyDeposit(applyDepositPopup?.deposit)}
          okText={t("Apply")}
        />
      </Portal>
    </Page >
  );
}

const statusBadge = (deposit: IInvoiceTransaction, t: TFunction) => {
  if (deposit.kind === "refund") {
    return <Badge type="warning">{t("secure.facility.order.invoices.deposits.016")}</Badge>;
  } else if (deposit.invoice_id !== null) {
    return <Badge type="gray">{t("secure.facility.order.invoices.deposits.022")}</Badge>;
  } else if (deposit.refunded) {
    return <Badge type="warning">{t("secure.facility.order.invoices.deposits.020")}</Badge>;
  } else if (deposit.partially_refunded) {
    return <Badge type="warning">{t("Partially Refunded")}</Badge>;
  } else {
    return <Badge type="success">{t("secure.facility.order.invoices.deposits.021")}</Badge>;
  }
};

const bookingDepositStatusBadge = (deposit: IBookingDeposit, t: TFunction) => {
  if (deposit.status === "refunded") {
    return <Badge outline type="warning">{t("Refunded")}</Badge>;
  } else if (deposit.status === "partially_refunded") {
    return <Badge outline type="warning">{t("Partially Refunded")}</Badge>;
  } else if (deposit?.status === "applied") {
    return <Badge outline type="success">{t("Applied")}</Badge>;
  } else if (deposit?.status === "pending") {
    return <Badge outline type="gray">{t("Pending")}</Badge>;
  } else {
    return <Badge outline type="gray">{capitalize(deposit?.status)}</Badge>;
  }

};

const eventName = (transaction: IInvoiceTransaction) => {
  if (transaction.tournament) {
    return transaction.tournament.name;
  } else if (transaction.league) {
    return transaction.league.name;
  } else if (transaction.hospitality_event) {
    return transaction.hospitality_event.name;
  } else {
    return "";
  }
};
